import React from "react";
import {
  Home,
  ListTodo,
  BookOpen,
  Network,
  UserRound,
  Settings,
  Menu,
  X,
  Sun,
  Moon,
  Plus,
} from "lucide-react";
import { useApplication } from "./ApplicationState";
import CalendarOverlay from "./chrome/CalendarOverlay";
import Sidebar from "./chrome/Sidebar";
import type { AppRoute } from "../navigation/routes";
import "./styles/layout.css";
import "../platforms/web/desktop/desktop.css";
import "../platforms/web/desktop/secondary-light-theme.css";
import "../platforms/web/desktop/light-theme.css";
import { dimensions } from "../platforms/web/desktop/dimensions";
import "../platforms/web/desktop/record-workspace.css";
import { useWebTheme } from "../platforms/web/desktop/useWebTheme";
const destinations: {
  route: AppRoute;
  label: string;
  icon: typeof Home;
}[] = [
  { route: "diary", label: "今日概览", icon: Home },
  { route: "missions", label: "任务中心", icon: ListTodo },
  { route: "records", label: "成长记录", icon: BookOpen },
];
export default function AppChrome({ children }: React.PropsWithChildren) {
  const { navigation, authUser, setShowSidebar, error, loadPeople, openDiaryCompose } = useApplication();
  const [expanded, setExpanded] = React.useState(false);
  const { theme, toggleTheme } = useWebTheme();
  const route = navigation.current.route;
  const active = destinations.find(
    (item) => item.route === route,
  );
  const activeDimension = dimensions.find((d) => d.route === route || d.related.includes(route));
  const pageLabel = active?.label || (route === "pan-spirit" ? "泛灵之网" : activeDimension ? `${activeDimension.glyph} · ${activeDimension.name}` : route === "settings" ? "设置" : "我的");
  const record = () => {
    setExpanded(false);
    openDiaryCompose("categories");
  };
  React.useEffect(() => {
    setExpanded(false);
    window.scrollTo(0, 0);
  }, [route]);
  React.useEffect(() => {
    if (!expanded) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [expanded]);
  const go = (next: AppRoute) => {
    navigation.navigate(next);
    setExpanded(false);
  };
  return (
    <div className="App app-web desktop-app" data-web-theme={theme}>
      <a
        href="#workspace-content"
        className="desktop-skip"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("workspace-content")?.focus();
        }}
      >
        跳到主要内容
      </a>
      <div className="desktop-mobile-bar">
        <button
          aria-label={expanded ? "关闭导航" : "打开导航"}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <X size={21} /> : <Menu size={21} />}
        </button>
        <strong>小本本</strong>
        <span>{pageLabel}</span>
        <button aria-label="随时记录" onClick={record}><Plus size={21} /></button>
      </div>
      {expanded && (
        <button
          className="desktop-nav-scrim"
          aria-label="关闭导航遮罩"
          onClick={() => setExpanded(false)}
        />
      )}
      <aside
        className={`desktop-sidebar ${expanded ? "expanded" : ""}`}
        aria-label="工作台导航"
      >
        <button className="desktop-brand" onClick={() => go("diary")}>
          <img src="/favicon.svg" alt="" />
          <strong>小本本</strong>
        </button>
        <button className="desktop-capture-button" onClick={record}><Plus size={19} />随手记一笔</button>
        <nav aria-label="主导航">
          <span className="desktop-nav-label">日常</span>
          {destinations.map((item) => {
            const Icon = item.icon;
            const selected = item === active;
            return (
              <button
                key={item.route}
                aria-current={selected ? "page" : undefined}
                className={selected ? "active" : ""}
                onClick={() => go(item.route)}
              >
                <Icon size={21} />
                <span>{item.label}</span>
              </button>
            );
          })}
          <span className="desktop-nav-label">人生六维</span>
          {dimensions.map((d) => (
            <button key={d.key} className={activeDimension === d ? "active" : ""}
              aria-current={activeDimension === d ? "page" : undefined}
              onClick={() => go(d.route)}>
              <span className="desktop-nav-glyph" aria-hidden="true">{d.glyph}</span>
              <span>{d.name}</span>
            </button>
          ))}
        </nav>
        <div className="desktop-sidebar-footer">
          <button onClick={() => go("pan-spirit")} aria-current={route === "pan-spirit" ? "page" : undefined}><Network size={20} /><span>泛灵之网</span></button>
          <button
            className="desktop-theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "切换到浅色皮肤" : "切换到深色皮肤"}
            title={theme === "dark" ? "切换到浅色皮肤" : "切换到深色皮肤"}
          >
            {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            <span>{theme === "dark" ? "浅色皮肤" : "深色皮肤"}</span>
          </button>
          <button
            aria-current={route === "mine" ? "page" : undefined}
            onClick={() => go("mine")}
          >
            <UserRound size={20} />
            <span>我的</span>
          </button>
          <button
            aria-current={route === "settings" ? "page" : undefined}
            onClick={() => go("settings")}
          >
            <Settings size={20} />
            <span>设置</span>
          </button>
          <button
            className="desktop-account"
            onClick={() => setShowSidebar(true)}
            aria-label="打开账户菜单"
          >
            <span className="desktop-avatar">
              {(authUser?.nickname || authUser?.username || "我").slice(0, 1)}
            </span>
            <span>
              {authUser?.nickname || authUser?.username}
              <small>账户与更多功能</small>
            </span>
          </button>
        </div>
      </aside>
      <main id="workspace-content" tabIndex={-1} className="desktop-content">
        <div className="desktop-breadcrumb">
          工作台 <span>/</span>{" "}
          <strong>
            {pageLabel}
          </strong>
        </div>
        {error && (route === 'people' || route === 'person') && <div className="desktop-notice" role="alert">{error}<button onClick={loadPeople}>重试</button></div>}
        {children}
      </main>
      <Sidebar />
      <CalendarOverlay />
    </div>
  );
}
